import _ from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { DateValueType } from "react-tailwindcss-datepicker";
import { activityAnalysisListName } from "src/api/config";
import Loading from "src/components/Loading";
import { ReportIcon } from "src/components/ui-icons/sidebar";
import TGDatePicker from "src/components/ui/TGDatePicker";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { useGetUsers } from "src/hooks/useGetUsers";
import { getItemsRequest } from "src/modules/spItems/api";
import { TGListWithSearch } from "src/pages/DashboardPage/acadia/components/Filters/ListsSearch";
import { LayoutWithAsideAndContent } from "src/pages/LayoutWithAsideAndContent";
import { CheckBoxListItem } from "src/types/main";
import { chunkArray } from "src/utils/array.utils";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { removeLocalItems } from "src/utils/localStorage";
import { prefix, removePrefix } from "src/utils/users.utils";
import { ResetFilters } from "../NotificationsReport";
import GeneralIcon from "./../../../components/ui-icons/GeneralIcon";
import { TGDisclosure } from "./../../../components/ui/TGDisclosure";

function ReportPageFilterBreadcrumbLayout() {
  return (
    <nav className="flex justify-between w-full" aria-label="Breadcrumb">
      <FlexCol className="grow">
        <ol className="inline-flex items-center">
          <li className="inline-flex items-center" aria-current="page">
            <div className="inline-flex items-center space-x-2 text-base font-medium cursor-pointer">
              <ReportIcon />
              <span>Отчеты</span>
            </div>
            <span className="px-3 text-body-color">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M16.2 8.55001L10.3781 2.64376C10.125 2.39064 9.73126 2.39064 9.47813 2.64376C9.22501 2.89689 9.22501 3.29064 9.47813 3.54376L14.2031 8.35314H2.25001C1.91251 8.35314 1.63126 8.63439 1.63126 8.97189C1.63126 9.30939 1.91251 9.61876 2.25001 9.61876H14.2594L9.47813 14.4844C9.22501 14.7375 9.22501 15.1313 9.47813 15.3844C9.59063 15.4969 9.75938 15.5531 9.92813 15.5531C10.0969 15.5531 10.2656 15.4969 10.3781 15.3563L16.2 9.45001C16.4531 9.19689 16.4531 8.80314 16.2 8.55001Z"
                  fill={"#637381"}
                />
              </svg>
            </span>
          </li>
          <li className="inline-flex items-center" aria-current="page">
            <div className="inline-flex items-center space-x-2 text-base font-medium cursor-pointer">
              <GeneralIcon />
              <span>Общий отчет</span>
            </div>
          </li>
        </ol>
      </FlexCol>
    </nav>
  );
}

type SubmitFiltersStateType = {
  locations: CheckBoxListItem[];
  departments: CheckBoxListItem[];
  periodDate: DateValueType;
};

function GeneralReport() {
  const [loading, setLoading] = useState(false);

  const [submitFilters, setSubmitFilters] = useState<SubmitFiltersStateType>({
    locations: [],
    departments: [],
    periodDate: {
      startDate: moment().startOf("month").toDate(),
      endDate: moment().add(1, "month").startOf("month").toDate(),
    },
  });

  const { users, downloading } = useGetUsers();

  const [employees, setEmployees] = useState<any[]>([]);

  function resetFilter(filterKey: keyof SubmitFiltersStateType) {
    setSubmitFilters((prev) => {
      const tempData = { ...prev };
      if (filterKey === "periodDate") {
        tempData.periodDate = {
          startDate: moment().startOf("month").toDate(),
          endDate: moment().add(1, "month").startOf("month").toDate(),
        };
      } else {
        tempData[filterKey] = [];
      }

      return tempData;
    });
  }

  function resetAllFilters() {
    setSubmitFilters({
      locations: [],
      departments: [],
      periodDate: {
        startDate: moment().startOf("month").toDate(),
        endDate: moment().add(1, "month").startOf("month").toDate(),
      },
    });
  }

  function changeSubmitFilters(submitData: SubmitFiltersStateType) {
    setSubmitFilters(submitData);
  }

  useAsyncEffect(async () => {
    if (users.length > 0) {
      setLoading(true);
      try {
        const filterUsers = await Promise.resolve(users).then((users) =>
          users.filter((user: { Department: string; Region: string }) => {
            // Проверка по Department и Region одновременно (через И)
            const departmentMatch = submitFilters.departments.length
              ? submitFilters.departments.some((d) => d.title === user.Department)
              : true; // Если фильтры для Department отсутствуют, считаем это условие выполненным

            const regionMatch = submitFilters.locations.length
              ? submitFilters.locations.some((d) => d.title === user.Region)
              : true; // Если фильтры для Region отсутствуют, считаем это условие выполненным

            // Фильтрация пройдет, только если оба условия выполнены
            return departmentMatch && regionMatch;
          })
        );

        setEmployees(filterUsers);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  }, [submitFilters.departments, submitFilters.locations]);

  useEffect(() => {
    if (!downloading) {
      setEmployees(users);
    }
  }, [users, downloading]);

  const filtersData = useMemo(
    () =>
      users.reduce(
        (acc, user: any) => {
          acc.locations.add(user.Region);
          acc.departments.add(user.Department);

          return acc;
        },
        { locations: new Set<string>(), departments: new Set<string>() }
      ),
    [users]
  );

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <LayoutWithAsideAndContent
          aside={
            <GeneralReportFilters
              filtersData={filtersData}
              submitFilters={submitFilters}
              resetAllFilters={resetAllFilters}
              resetFilter={resetFilter}
              changeSubmitFilters={changeSubmitFilters}
            />
          }
          content={
            <FlexCol className="w-full">
              <FlexRow className="px-7.5 h-[76px] border-b justify-between items-center shrink-0">
                <FlexCol>
                  <ReportPageFilterBreadcrumbLayout />
                </FlexCol>
              </FlexRow>
              <FlexRow className="px-7.5 py-7.5 h-[calc(100vh-76px)] ">
                <FlexCol className="w-full gap-5">
                  <GeneralUserDiagrams
                    periodDate={submitFilters.periodDate}
                    employees={employees}
                    loading={downloading}
                  />
                </FlexCol>
              </FlexRow>
            </FlexCol>
          }
        />
      )}
    </>
  );
}

export default GeneralReport;

function GeneralUserDiagrams({
  periodDate,
  employees,
  loading,
}: {
  periodDate: DateValueType;
  employees: any[];
  loading: boolean;
}) {
  const dateTitle = useMemo(() => {
    // Получение текущей даты
    const startDate = periodDate?.startDate ? moment(periodDate?.startDate) : null;
    const endDate = periodDate?.endDate ? moment(periodDate?.endDate) : null;

    // Первый день текущего месяца
    const currentMonth = startDate ? startDate.format("DD.MM.YYYY") : "---";

    // Последний день текущего месяца
    const nextMonth = endDate ? endDate.format("DD.MM.YYYY") : "---";

    return `от ${currentMonth} до ${nextMonth}`;
  }, [periodDate]);

  return (
    <FlexCol className="w-full gap-5">
      <UniqueEnterCard baseLoading={loading} employees={employees} periodDate={periodDate} dateTitle={dateTitle} />
      <ActiveUsersCard baseLoading={loading} employees={employees} periodDate={periodDate} dateTitle={dateTitle} />
    </FlexCol>
  );
}

function UniqueEnterCard({
  dateTitle,
  periodDate,
  employees,
  baseLoading,
}: {
  dateTitle: string;
  periodDate: DateValueType;
  employees: any[];
  baseLoading: boolean;
}) {
  const [loading, setLoading] = useState(baseLoading);
  const [results, setResults] = useState<any[]>([]);

  useAsyncEffect(async () => {
    if (employees.length === 0) return;

    // Разбиваем массив employees на чанки по 50 элементов
    const chunks = chunkArray(employees, 50);

    const chunkPromises = [];

    for (const chunk of chunks) {
      chunkPromises.push(
        getItemsRequest({
          listName: activityAnalysisListName,
          filters: {
            isVisit: true,
            AuthorEmail: chunk.map((emp) => removePrefix(emp.AccountName, prefix)), // Используем текущий чанк
            Created: {
              start: periodDate?.startDate,
              end: periodDate?.endDate,
            },
          },
          viewFields: ["ID", "Author"],
        })
      );
    }

    setLoading(true);
    try {
      const responses = await Promise.all(chunkPromises);
      const result = _(responses)
        .map("items") // Получаем массивы items
        .filter((items) => !_.isEmpty(items)) // Фильтруем непустые массивы
        .flatMap((items) => _.map(items, "AuthorEmail")) // Извлекаем AuthorEmail и объединяем в один массив
        .uniq()
        .value();

      // Сохраняем результаты в состояние
      setResults(result);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [periodDate, employees]);

  let usersPercent = useMemo(() => {
    const percent = (results.length / employees.length) * 100;

    return _.isNaN(percent) ? "0" : percent.toFixed(2);
  }, [results]);

  return (
    <FlexRow className="px-[25px] py-5 shadow-custom rounded-lg bg-white">
      <FlexCol className="w-full">
        <FlexRow className="justify-center mb-[15px]">
          <h5 className="text-sm font-medium text-black">Количество уникальных входов в систему</h5>
        </FlexRow>
        <FlexRow className="items-center space-x-4">
          <FlexCol className="w-full">
            <TGRange
              percent={usersPercent}
              title={loading ? "Идет подсчет..." : `${usersPercent}% (${results.length})`}
              color={"#171C8F"}
            />
          </FlexCol>
          <FlexCol className="w-[200px] shrink-0">{dateTitle}</FlexCol>
        </FlexRow>
      </FlexCol>
    </FlexRow>
  );
}

function ActiveUsersCard({
  dateTitle,
  periodDate,
  employees,
  baseLoading,
}: {
  dateTitle: string;
  periodDate: DateValueType;
  employees: any[];
  baseLoading: boolean;
}) {
  const [loading, setLoading] = useState(baseLoading);
  const [results, setResults] = useState<any[]>([]);

  useAsyncEffect(async () => {
    if (employees.length === 0) return;

    // Разбиваем массив employees на чанки по 50 элементов
    const chunks = chunkArray(employees, 50);

    const chunkPromises = [];

    for (const chunk of chunks) {
      chunkPromises.push(
        getItemsRequest({
          listName: activityAnalysisListName,
          filters: {
            isActive: true,
            AuthorEmail: chunk.map((emp) => removePrefix(emp.AccountName, prefix)), // Используем текущий чанк
            Created: {
              start: periodDate?.startDate,
              end: periodDate?.endDate,
            },
          },
        })
      );
    }

    setLoading(true);
    try {
      const responses = await Promise.all(chunkPromises);
      const result = _(responses)
        .map("items") // Получаем массивы items
        .filter((items) => !_.isEmpty(items)) // Фильтруем непустые массивы
        .flatMap((items) => _.map(items, "AuthorEmail")) // Извлекаем AuthorEmail и объединяем в один массив
        .uniq()
        .value();

      // Сохраняем результаты в состояние
      setResults(result);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [periodDate, employees]);

  let usersPercent = useMemo(() => {
    const percent = (results.length / employees.length) * 100;

    return _.isNaN(percent) ? "0" : percent.toFixed(2);
  }, [results]);

  return (
    <FlexRow className="px-[25px] py-5 shadow-custom rounded-lg bg-white">
      <FlexCol className="w-full">
        <FlexRow className="justify-center mb-[15px]">
          <h5 className="text-sm font-medium text-black">Количество активных пользователей</h5>
        </FlexRow>
        <FlexRow className="items-center space-x-4">
          <FlexCol className="w-full">
            <TGRange
              percent={usersPercent}
              title={loading ? "Идет подсчет..." : `${usersPercent}% (${results.length})`}
              color={"#2D68F8"}
            />
          </FlexCol>
          <FlexCol className="w-[200px] shrink-0">{dateTitle}</FlexCol>
        </FlexRow>
      </FlexCol>
    </FlexRow>
  );
}

function TGRange({ percent, title, color = "blue" }: { percent: string; title: string; color: string }) {
  return (
    <div className="relative w-full overflow-hidden rounded-lg">
      <div className="w-full h-10 bg-gray-300 rounded-lg"></div>
      <div
        style={{
          width: `${percent}%`,
          transition: "width 0.5s ease-in-out",
          backgroundColor: color,
        }}
        className="absolute top-0 left-0 flex items-center justify-center h-10 text-sm text-white rounded-lg"
      ></div>
      <div
        style={{ color: +percent >= 60 ? "#fff" : "#171C8F" }}
        className="absolute top-0 bottom-0 left-0 right-0 pt-2 text-center text-white"
      >
        {title}
      </div>
    </div>
  );
}

function GeneralReportFilters({
  filtersData,
  submitFilters,
  changeSubmitFilters,
  resetAllFilters,
  resetFilter,
}: {
  filtersData: { locations: Set<string>; departments: Set<string> };
  submitFilters: SubmitFiltersStateType;
  changeSubmitFilters: (e: SubmitFiltersStateType) => void;
  resetAllFilters: () => void;
  resetFilter: (filterKey: keyof SubmitFiltersStateType) => void;
}) {
  function changeLocations(item: CheckBoxListItem) {
    const isFound = submitFilters.locations.find((el: CheckBoxListItem) => el.id === item.id) as CheckBoxListItem;

    let tempArray: CheckBoxListItem[] = [...submitFilters.locations];

    if (isFound) {
      tempArray = submitFilters.locations.filter((l) => l.id !== item.id);
    } else {
      tempArray = [...submitFilters.locations, item];
    }

    changeSubmitFilters({ ...submitFilters, locations: tempArray });
  }

  function changeDepartments(item: CheckBoxListItem) {
    const isFound = submitFilters.departments.find((el: CheckBoxListItem) => el.id === item.id) as CheckBoxListItem;

    let tempArray: CheckBoxListItem[] = [...submitFilters.departments];

    if (isFound) {
      tempArray = submitFilters.departments.filter((l) => l.id !== item.id);
    } else {
      tempArray = [...submitFilters.departments, item];
    }

    changeSubmitFilters({ ...submitFilters, departments: tempArray });
  }

  function changePeriodDate(value: DateValueType) {
    const obj = {
      startDate: value!.startDate ? new Date(value!.startDate) : null,
      endDate: value!.endDate ? new Date(value!.endDate) : null,
    };

    changeSubmitFilters({ ...submitFilters, periodDate: obj });
  }

  const isFiltersEmpty = useMemo(() => {
    return (
      !!submitFilters.locations.length ||
      !!submitFilters.departments.length ||
      !(
        moment(submitFilters.periodDate?.startDate).isSame(moment().startOf("month").toDate()) &&
        moment(submitFilters.periodDate?.endDate).isSame(moment().add(1, "month").startOf("month").toDate())
      )
    );
  }, [
    submitFilters.locations,
    submitFilters.departments,
    submitFilters.periodDate?.startDate,
    submitFilters.periodDate?.endDate,
  ]);

  useEffect(() => {
    return () => removeLocalItems(["Region", "Department"]);
  }, []);

  return (
    <FlexCol className="overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB] h-full">
      {isFiltersEmpty && <ResetFilters onReset={resetAllFilters} />}
      <FlexRow className="w-full">
        <TGDisclosure title="Локация" wrapperClassName="w-full">
          <TGListWithSearch
            listData={[...filtersData.locations].map((d) => ({ id: d || Date.now(), title: d || "No Data" }))}
            checkedItems={(item) => changeLocations(item)}
            items={submitFilters.locations}
          />
          <FlexRow
            className="group items-center text-[12px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap"
            onClick={() => resetFilter("locations")}
          >
            <FlexCol className="group-hover:underline">Сбросить фильтры</FlexCol>
            <FlexCol className="pt-1">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_33_4954)">
                  <path
                    d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
                    fill="#637381"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_33_4954">
                    <rect width="12" height="12" fill="white" transform="translate(0 12) rotate(-90)" />
                  </clipPath>
                </defs>
              </svg>
            </FlexCol>
          </FlexRow>
        </TGDisclosure>
      </FlexRow>
      <FlexRow>
        <TGDisclosure title="Отдел" wrapperClassName="w-full">
          <TGListWithSearch
            listData={[...filtersData.departments].map((d) => ({ id: d || Date.now(), title: d || "No Data" }))}
            checkedItems={(item) => changeDepartments(item)}
            items={submitFilters.departments}
          />
          <FlexRow
            className="group items-center text-[12px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap"
            onClick={() => resetFilter("departments")}
          >
            <FlexCol className="group-hover:underline">Сбросить фильтры</FlexCol>
            <FlexCol className="pt-1">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_33_4954)">
                  <path
                    d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
                    fill="#637381"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_33_4954">
                    <rect width="12" height="12" fill="white" transform="translate(0 12) rotate(-90)" />
                  </clipPath>
                </defs>
              </svg>
            </FlexCol>
          </FlexRow>
        </TGDisclosure>
      </FlexRow>
      <FlexRow>
        <TGDisclosure title="Даты" wrapperClassName="w-full">
          <FlexCol className="relative z-[100] w-full gap-y-3.5">
            <h4 className={"font-normal"}>От и До</h4>
            <TGDatePicker value={submitFilters.periodDate} onChange={changePeriodDate} popoverDirection="down" />
            <FlexRow
              className="group items-center text-[12px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap"
              onClick={() => resetFilter("periodDate")}
            >
              <FlexCol className="group-hover:underline">Сбросить фильтры</FlexCol>
              <FlexCol className="pt-1">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_33_4954)">
                    <path
                      d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
                      fill="#637381"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_33_4954">
                      <rect width="12" height="12" fill="white" transform="translate(0 12) rotate(-90)" />
                    </clipPath>
                  </defs>
                </svg>
              </FlexCol>
            </FlexRow>
          </FlexCol>
        </TGDisclosure>
      </FlexRow>
    </FlexCol>
  );
}
